export const SEVERITY = Object.freeze({
  OK: 0,
  WARNING: 1,
  ERROR: 2,
  CRITICAL: 3,
  NA: 4,
});

export const SEVERITY_LABEL = Object.freeze({
  0: "OK",
  1: "Warning",
  2: "Error",
  3: "Critical",
  4: "Unknown",
});

export const HEALTH = Object.freeze({
  HEALTHY: "healthy",
  WARNING: "warning",
  CRITICAL: "critical",
  UNKNOWN: "unknown",
});

export const HEALTH_ORDER = [HEALTH.HEALTHY, HEALTH.WARNING, HEALTH.CRITICAL, HEALTH.UNKNOWN];

export const HEALTH_RANK = Object.freeze({
  [HEALTH.HEALTHY]: 0,
  [HEALTH.UNKNOWN]: 1,
  [HEALTH.WARNING]: 2,
  [HEALTH.CRITICAL]: 3,
});

export const severityToHealth = (severity) => {
  const s = Number(severity);
  if (s === SEVERITY.OK) return HEALTH.HEALTHY;
  if (s === SEVERITY.WARNING || s === SEVERITY.ERROR) return HEALTH.WARNING;
  if (s === SEVERITY.CRITICAL) return HEALTH.CRITICAL;
  return HEALTH.UNKNOWN;
};

export const statusToHealthy = (status) => status === true || status === "true";

export const ICCC_CATEGORY_ORDER = ["PA", "CCTV", "SCADA", "TELEPHONY", "SENSORS", "UPS"];

// Category presentation catalog: [icon, label, ...aliases].
// Each alias (the category code AND/OR its m_category description) is registered
// case-insensitively, because the dashboard groups by description while other
// views key on the code — so the right Material icon resolves either way.
// To give a new device category an icon in Group-wise Health, add a row here.
const CATEGORY_CATALOG = [
  // ICCC subsystems (code === description on ICCC servers)
  ["campaign", "PA", "PA"],
  ["videocam", "CCTV", "CCTV"],
  ["memory", "SCADA", "SCADA"],
  ["call", "Telephony", "TELEPHONY"],
  ["sensors", "Sensors", "SENSORS"],
  ["battery_charging_full", "UPS", "UPS"],
  ["router", "Network", "NETWORK"],
  // IP phones / mobile endpoints
  ["phone", "2 Line IP Phone", "IP2LP", "2 Line IP Phone"],
  ["phone_in_talk", "6 Line IP Phone", "IP6LP", "6 Line IP Phone"],
  ["dialpad", "IP 2 Line (Gig)", "IP2LG", "IP 2 Line Phone (Gigabit)"],
  ["perm_phone_msg", "6 LP + Media GW", "IP6LPL", "IP 6 LP Phones Attached with Media gateway"],
  ["video_call", "Video IP Phone", "VIP", "Video IP Phone"],
  ["smartphone", "UE", "IMSI", "UE"],
  // Servers
  ["dns", "App Server", "Server", "Application Server"],
  ["ring_volume", "Call Server", "CALL-SERVER", "Call Server"],
  ["extension", "Feature Server", "FEATURE-SERVER", "Feature Server"],
  // Gateways & remote units
  ["router", "Network Gateway", "GW", "Network Gateway"],
  ["device_hub", "Media GW ICC3", "ICC3", "Media Gateway - ICC3"],
  ["device_hub", "Media GW ICC4", "ICC4", "Media Gateway - ICC4"],
  ["phone_forwarded", "Trunk Gateway", "TRUNK-GATEWAY", "Trunk Gateway"],
  ["settings_input_antenna", "RLU", "RLU"],
  // Switches
  ["lan", "Layer2 Switch", "L2", "Layer2 Switch"],
  ["hub", "Layer3 Switch", "L3", "Layer3 Switch"],
  // Compute & virtualization
  ["computer", "User PC", "PC", "User PC"],
  ["laptop", "Laptop", "LAPTOP", "Laptop"],
  ["cloud", "Virtualization", "Virtual-Machine", "Virtualization"],
  ["layers", "Virtual Platform", "Xenserver", "Virtual Platform"],
  // Help desks
  ["headset_mic", "Audio Help Desk", "AHD", "Audio Help Desk"],
  ["video_chat", "Video Help Desk", "VHD", "Video Help Desk"],
  // Field / unknown
  ["explore", "Rover", "Rover"],
  ["device_unknown", "Unidentified", "un-identified", "Un Identified"],
  ["help", "Unknown", "UNKNOWN"],
];

const CATEGORY_META = CATEGORY_CATALOG.reduce((map, [icon, label, ...aliases]) => {
  aliases.forEach((alias) => {
    map[String(alias).trim().toUpperCase()] = { label, icon };
  });
  return map;
}, {});

// Reverse lookup: any known alias (code OR description, upper-cased) -> the
// canonical category CODE (the first alias of each catalog row). Lets the device
// grid map a selected group label ("2 LINE IP PHONE") to the real
// scada_assets.category code ("IP2LP") with NO network round-trip.
const CODE_BY_ALIAS = CATEGORY_CATALOG.reduce((map, [, , code, ...rest]) => {
  [code, ...rest].forEach((alias) => {
    map[String(alias).trim().toUpperCase()] = code;
  });
  return map;
}, {});

export const normalizeCategoryCode = (code) => String(code || "UNKNOWN").trim().toUpperCase();

export const categoryMeta = (code) => {
  const key = normalizeCategoryCode(code);
  return CATEGORY_META[key] || { label: code || "Unknown", icon: "developer_board" };
};

export const compareCategories = (a, b) => {
  const ra = ICCC_CATEGORY_ORDER.indexOf(normalizeCategoryCode(a.code));
  const rb = ICCC_CATEGORY_ORDER.indexOf(normalizeCategoryCode(b.code));
  const ka = ra === -1 ? Number.MAX_SAFE_INTEGER : ra;
  const kb = rb === -1 ? Number.MAX_SAFE_INTEGER : rb;
  if (ka !== kb) return ka - kb;
  return (b.total || 0) - (a.total || 0);
};

export const DEFAULT_POLL_MS = 15000;

export const BOTTOM_ROW_HEIGHT = 560;

export const DEVICE_PAGE_SIZE = 10;

export const TIME_RANGES = [
  { value: "all", label: "All time", ms: null },
  { value: "24h", label: "Last 24 hours", ms: 24 * 60 * 60 * 1000 },
  { value: "7d", label: "Last 7 days", ms: 7 * 24 * 60 * 60 * 1000 },
  { value: "30d", label: "Last 30 days", ms: 30 * 24 * 60 * 60 * 1000 },
  { value: "90d", label: "Last 90 days", ms: 90 * 24 * 60 * 60 * 1000 },
];

export const timeRangeMs = (value) =>
  (TIME_RANGES.find((r) => r.value === value) || TIME_RANGES[0]).ms;

// Coral AdvanceFilter has no "equals" operator: a string column is matched for
// equality with a from+to pair. The SCADAAsset/list backend compares the column
// case-sensitively, so send the value in its stored (exact) case — the resolved
// code already matches the case in scada_assets.category (e.g. "IP2LP").
export const categoryEqualsFilter = (category) => {
  if (!category) return [];
  const value = String(category).toLowerCase();
  return [
    { direction: "from", dataType: "string", fieldName: "category", value },
    { direction: "to", dataType: "string", fieldName: "category", value },
  ];
};

// The group tiles/summary key on the m_category description (e.g. "6 LINE IP
// PHONE") while scada_assets.category stores the code (e.g. "IP6LP"). Translate a
// selected group to the actual code: match by code first, then by description,
// else return as-is (covers ICCC servers where code === description).
export const resolveCategoryCode = (selected, categories = []) => {
  if (!selected) return null;
  const up = String(selected).trim().toUpperCase();
  // 1) static catalog (synchronous — covers all standard Coral categories)
  if (CODE_BY_ALIAS[up]) return CODE_BY_ALIAS[up];
  // 2) live m_category list (covers any category not in the catalog)
  const byCode = categories.find((c) => String(c.code || "").toUpperCase() === up);
  if (byCode) return byCode.code;
  const byDesc = categories.find((c) => String(c.description || "").toUpperCase() === up);
  if (byDesc) return byDesc.code;
  return selected;
};
